"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
function CollaborationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-start justify-center h-full min-h-[400px] space-y-6 animate-in fade-in duration-700">
        <div className="w-12 h-px bg-primary mb-4"></div>
        <h3 className="text-2xl font-default text-foreground">Consultation Requested</h3>
        <p className="text-muted-foreground font-default text-sm max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-md border-border hover:bg-primary hover:text-primary-foreground hover:border-primary font-default text-xs tracking-widest uppercase h-12 px-8">
          Submit Another Request
        </Button>
      </div>;
  }
  const inputStyles = "flex w-full bg-transparent border-0 border-b border-border/40 rounded-md px-0 py-3 text-base shadow-none transition-colors focus-visible:outline-none focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/30 font-default";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="Email Address" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Property Type</FormLabel>
              <FormControl>
                <Input placeholder="Residential or Commercial" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="Phone Number" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="project_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Service Required</FormLabel>
              <FormControl>
                <select className={`${inputStyles} appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%24%2024%22%20fill%3D%22none%22%20stroke%3D%22%23666%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[position:right_0_center] bg-[length:1em] pr-8 text-foreground/80`} {...field}>
                  <option value="" disabled>Select service type</option>
                  <option value="Landscape Design">Landscape Design</option>
                  <option value="Fencing Installation">Fencing Installation</option>
                  <option value="Lawn Maintenance">Lawn Maintenance</option>
                  <option value="Custom Patios">Custom Patios</option>
                  <option value="Irrigation Repair">Irrigation Repair</option>
                  <option value="Other">Other Services</option>
                </select>
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Estimated Budget</FormLabel>
              <FormControl>
                <select className={`${inputStyles} appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%24%2024%22%20fill%3D%22none%22%20stroke%3D%22%23666%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[position:right_0_center] bg-[length:1em] pr-8 text-foreground/80`} {...field}>
                  <option value="" disabled>Select budget range (Optional)</option>
                  <option value="Under $2,500">Under $2,500</option>
                  <option value="$2,500 - $7,500">$2,500 - $7,500</option>
                  <option value="$7,500 - $15,000">$7,500 - $15,000</option>
                  <option value="$15,000+">$15,000+</option>
                </select>
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="details" rules={{
      required: "Project details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Project Details</FormLabel>
            <FormControl>
              <textarea placeholder="Describe your vision for your outdoor space, including any specific features like patios or irrigation needs..." className={`${inputStyles} min-h-[120px] resize-y`} {...field} />
            </FormControl>
            <p className="text-[10px] text-muted-foreground/60 font-default mt-2">Detailed information helps us provide a more accurate initial estimate.</p>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10 text-destructive font-default text-xs">
          [ERROR]: {submitError}
        </div>}

      <div className="pt-8">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md border border-border/40 bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 font-default text-xs tracking-[0.2em] uppercase group relative overflow-hidden">
          <span className="relative z-10 flex items-center justify-center">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-3 size-4" />
                Processing...
              </> : 'Request Free Consultation'}
          </span>
          <div className="absolute inset-0 bg-primary translate-y-[100%] group-hover:translate-y-0 transition-transform duration-300 ease-in-out z-0"></div>
        </Button>
      </div>
    </Form>;
}
export default function CollaborateWithUs() {
  return <section data-section-id="222895" id="start-your-project" className="dark bg-background text-foreground py-24 md:py-32 px-6 md:px-12 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/20 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/20 to-transparent"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-md blur-3xl pointer-events-none opacity-50"></div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 relative z-10">
        
        {}
        <div className="lg:col-span-4 flex flex-col justify-between">
          <div>
            <h2 className="text-4xl md:text-5xl font-default font-light tracking-tight mb-12">
              Start Your<br />Project
            </h2>
            
            <div className="space-y-10 font-default text-sm">
              <div className="group">
                <h3 className="text-muted-foreground/60 mb-3 uppercase tracking-widest text-[10px] flex items-center">
                  <span className="w-4 h-px bg-muted-foreground/40 mr-3 transition-all group-hover:w-8 group-hover:bg-primary"></span>
                  Our Office
                </h3>
                <p className="text-foreground/80 leading-relaxed">
                  Campos V Landscaping HQ<br />
                  Green Valley, WA 98001
                </p>
              </div>

              <div className="group">
                <h3 className="text-muted-foreground/60 mb-3 uppercase tracking-widest text-[10px] flex items-center">
                  <span className="w-4 h-px bg-muted-foreground/40 mr-3 transition-all group-hover:w-8 group-hover:bg-primary"></span>
                  Contact Support
                </h3>
                <p className="text-foreground/80 leading-relaxed">
                  (774) 540-0459
                </p>
              </div>

              <div className="group">
                <h3 className="text-muted-foreground/60 mb-3 uppercase tracking-widest text-[10px] flex items-center">
                  <span className="w-4 h-px bg-muted-foreground/40 mr-3 transition-all group-hover:w-8 group-hover:bg-primary"></span>
                  Email Inquiries
                </h3>
                <p className="text-foreground/80 leading-relaxed">
                  Ericcampos124@gmail.com
                </p>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-border/20">
            <p className="font-default text-[10px] text-muted-foreground/50 uppercase tracking-widest">
              Service Hours<br />
              Mon-Fri: 7am - 5pm<br />
              Sat: 8am - 2pm
            </p>
          </div>
        </div>

        {}
        <div className="lg:col-span-8 bg-card/30 backdrop-blur-sm border border-border/10 p-8 md:p-12 shadow-xl shadow-black/20 rounded-md">
          <FormProvider formKey="start_project_form_get_a_quote" formId="start_project_form_get_a_quote" sectionName="CollaborateWithUs" defaultValues={{
          name: "",
          email: "",
          company: "",
          phone: "",
          project_type: "",
          budget: "",
          details: ""
        }} submitText="Request Free Consultation" successMessage="Your landscaping inquiry has been received. Our design team will review your details and contact you within 24 hours to schedule your free consultation.">
            <CollaborationFormInner />
          </FormProvider>
        </div>

      </div>
    </section>;
}